import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Button, Checkbox, Form, Icon, Input, Label, List, Modal, TextArea } from 'semantic-ui-react';

import { EmptyCollectionMessage } from '@src/views/components/util/messaging/EmptyCollectionMessage';
import { LoadingIndicator }       from '@src/views/components/util/loading/LoadingIndicator';
import { useCentralCalls }        from '@src/rest/CentralCalls';


// --- Types ---

export type SurveyQuestionInputType = 'DROPDOWN' | 'MULTISELECT' | 'TEXT' | 'NUMBER';

// UI model for a survey question. Field names are the component's own; the mapping to/from
// the database (PascalCase columns) happens in the data-access layer below.
export interface SurveyQuestion {
    id:              string;
    questionText:    string;
    description:     string;
    type:            SurveyQuestionInputType;
    surveyTagIds:    string[];
    options:         string[];
    isRequired:      boolean;
}

interface SurveyTag {
    id:      string;
    tagName: string;
}


// --- Constants ---

function getTypeBadgeLabel( question: SurveyQuestion ): string {
    switch( question.type ){
        case 'DROPDOWN':
        case 'MULTISELECT':
            return 'SELECTION';
        case 'TEXT':
            return 'FREE TEXT';
        case 'NUMBER':
            return 'NUMBER';
    }
}

const newId = () => uuid();


// --- REST data access ---
// TableName values must match the DbSet property names on CorvalContext (plural).
const SURVEY_QUESTION_TABLE = 'SurveyQuestions';
const SURVEY_QUESTION_OPTION_TABLE = 'SurveyQuestionOptions';
const SURVEY_TAG_TABLE = 'SurveyTags';
const SURVEY_QUESTION_TAG_TABLE = 'SurveyQuestionTags';

const normalizeOptionText = ( value: string ) => value.trim().toLowerCase();
const isRowDeleted = ( value: boolean | number | null | undefined ) => {
    if( typeof value === 'boolean' ){
        return value;
    }

    return Number( value ?? 0 ) === 1;
};

// Shapes returned by the generic TableSelect endpoint. Although the DB columns are PascalCase,
// this endpoint serializes responses as camelCase (see the "results come out lowercase fields"
// note in AIObjectPicker.tsx), so reads use camelCase. Writes below stay PascalCase — the
// backend matches those column names case-insensitively.
interface SurveyQuestionRow {
    id:           string;
    questionType: string;
    question:     string;
    description:  string | null;
    required?:    boolean | number | null;
}

interface SurveyQuestionOptionRow {
    id:               string;
    surveyQuestionId: string;
    optionText:       string;
    isDeleted?:       boolean | number | null;
}

interface SurveyTagRow {
    id:      string;
    tagName: string;
}

interface SurveyQuestionTagRow {
    id:               string;
    surveyQuestionId: string;
    surveyTagId?:     string | null;
}

function toLocalQuestion(
    row: SurveyQuestionRow,
    optionRows: SurveyQuestionOptionRow[],
    surveyTagIds: string[],
): SurveyQuestion {
    const isRequired = typeof row.required === 'boolean'
        ? row.required
        : Number( row.required ?? 0 ) === 1;

    return {
        id:              row.id,
        questionText:    row.question,
        description:     row.description ?? '',
        type:            ( row.questionType as SurveyQuestionInputType ) ?? 'TEXT',
        surveyTagIds,
        options:         optionRows.map( o => o.optionText ),
       
        isRequired,
    };
}


// --- Edit Question Modal ---

interface EditQuestionModalProps {
    question:       SurveyQuestion | null;
    availableTags:  SurveyTag[];
    open:           boolean;
    onClose:        () => void;
    onSave:         ( question: SurveyQuestion ) => void;
    onCreateTag:    ( tagName: string ) => Promise<string | null>;
}

const BLANK_QUESTION: Omit<SurveyQuestion, 'id'> = {
    questionText:    '',
    description:     '',
    type:            'DROPDOWN',
    surveyTagIds:    [],
    options:         [ '' ],
    isRequired:      false,
};

function EditQuestionModal( {
    question,
    availableTags,
    open,
    onClose,
    onSave,
    onCreateTag,
}: EditQuestionModalProps ){

    const [ draft, setDraft ]                     = useState<SurveyQuestion>(question ?? { ...BLANK_QUESTION, id: newId() }),
          [ isCreateTagOpen, setIsCreateTagOpen ] = useState( false ),
          [ newTagName, setNewTagName ]           = useState( '' ),
          [ isCreatingTag, setIsCreatingTag ]     = useState( false ),
          [ pendingTagId, setPendingTagId ]       = useState<string | undefined>(undefined);

    React.useEffect( () => {
        setDraft( question ? { ...question } : { ...BLANK_QUESTION, id: newId() } );
    }, [ question, open ] );

    const hasOptions = draft.type === 'DROPDOWN' || draft.type === 'MULTISELECT';

    const setField = <K extends keyof SurveyQuestion>( key: K, value: SurveyQuestion[ K ] ) => {
        setDraft( prev => ( { ...prev, [ key ]: value } ) );
    };

    const updateOption = ( index: number, value: string ) => {
        const updated = [ ...draft.options ];
        updated[ index ] = value;
        setField( 'options', updated );
    };

    const addOption = () => setField( 'options', [ ...draft.options, '' ] );

    const copyOption = ( index: number ) => {
        const updated = [ ...draft.options ];
        updated.splice( index + 1, 0, draft.options[ index ] );
        setField( 'options', updated );
    };

    const removeOption = ( index: number ) => {
        setField( 'options', draft.options.filter( ( _, i ) => i !== index ) );
    };

    const TYPE_BUTTONS: { type: SurveyQuestionInputType; label: string }[] = [
        { type: 'TEXT', label: 'Text' },
        { type: 'DROPDOWN', label: 'Dropdown' },
        { type: 'MULTISELECT', label: 'Multi-select' },
        { type: 'NUMBER', label: 'Number' },
    ];

    const tagOptions = availableTags
        .filter( tag => !draft.surveyTagIds.includes( tag.id ) )
        .map( tag => ( {
        key:   tag.id,
        value: tag.id,
        text:  tag.tagName,
        } ) );

    const addPendingTag = () => {
        if( !pendingTagId || draft.surveyTagIds.includes( pendingTagId ) ){
            return;
        }

        setField( 'surveyTagIds', [ ...draft.surveyTagIds, pendingTagId ] );
        setPendingTagId( undefined );
    };

    const removeTag = ( tagId: string ) => {
        setField( 'surveyTagIds', draft.surveyTagIds.filter( id => id !== tagId ) );
    };

    const handleCreateTag = async () => {
        const trimmedName = newTagName.trim();
        if( !trimmedName ){
            return;
        }

        setIsCreatingTag( true );
        try {
            const createdTagId = await onCreateTag( trimmedName );
            if( createdTagId ){
                setField( 'surveyTagIds', [ ...draft.surveyTagIds, createdTagId ] );
                setNewTagName( '' );
                setIsCreateTagOpen( false );
            }
        } finally {
            setIsCreatingTag( false );
        }
    };

    return (
        <>
            <Modal open={ open } onClose={ onClose } size="small">

                <Modal.Header style={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } }>
                    <span>{ question ? 'Edit Question' : 'Add Question' }</span>
                    <Icon
                        name="times"
                        style={ { cursor: 'pointer', color: '#aaa', fontSize: '1rem', margin: 0 } }
                        onClick={ onClose }
                    />
                </Modal.Header>

                <Modal.Content scrolling>
                    <Form>

                    {/* --- Question Type --- */}
                    <Form.Field>
                        <label>Question Type</label>
                        <Button.Group fluid>
                            { TYPE_BUTTONS.map( btn => (
                                <Button
                                    key={ btn.type }
                                    type="button"
                                    basic={ draft.type !== btn.type }
                                    color={ draft.type === btn.type ? 'purple' : undefined }
                                    onClick={ () => setField( 'type', btn.type ) }
                                    style={ { padding: '10px 6px' } }>
                                    <div style={ { textAlign: 'center' } }>
                                        { btn.type === 'TEXT' && (
                                            <strong style={ { display: 'block', fontSize: '1.1em', marginBottom: '3px' } }>T</strong>
                                        ) }
                                        { btn.type === 'DROPDOWN' && (
                                            <Icon name="chevron down" style={ { display: 'block', margin: '0 auto 3px' } }/>
                                        ) }
                                        { btn.type === 'MULTISELECT' && (
                                            <Icon name="check square outline" style={ { display: 'block', margin: '0 auto 3px' } }/>
                                        ) }
                                        { btn.type === 'NUMBER' && (
                                            <strong style={ { display: 'block', fontSize: '0.85em', marginBottom: '3px' } }>123</strong>
                                        ) }
                                        <span style={ { fontSize: '0.75em' } }>{ btn.label }</span>
                                    </div>
                                </Button>
                            ) ) }
                        </Button.Group>
                    </Form.Field>

                    <Form.Field>
                        <label>Survey Tags</label>
                        <div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
                            <Form.Select
                                fluid
                                placeholder={ tagOptions.length ? 'Select a tag' : 'All tags selected' }
                                options={ tagOptions }
                                value={ pendingTagId }
                                disabled={ tagOptions.length === 0 }
                                onChange={ ( _, d ) => {
                                    const nextTagId = typeof d.value === 'string' ? d.value : undefined;
                                    setPendingTagId( nextTagId );
                                } }
                            />
                            <Button
                                type="button"
                                basic
                                size="small"
                                disabled={ !pendingTagId }
                                onClick={ addPendingTag }>
                                Add
                            </Button>
                            <Button
                                type="button"
                                basic
                                icon
                                size="small"
                                title="Add new tag"
                                onClick={ () => setIsCreateTagOpen( true ) }>
                                <Icon name="plus"/>
                            </Button>
                        </div>
                        { draft.surveyTagIds.length > 0 && (
                            <div style={ { marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' } }>
                                { draft.surveyTagIds.map( tagId => {
                                    const tag = availableTags.find( t => t.id === tagId );
                                    if( !tag ){
                                        return null;
                                    }

                                    return (
                                        <Label key={ tagId } size="small" style={ { margin: 0 } }>
                                            { tag.tagName }
                                            <Icon
                                                name="delete"
                                                link
                                                onClick={ () => removeTag( tagId ) }
                                                style={ { marginLeft: '6px' } }
                                            />
                                        </Label>
                                    );
                                } ) }
                            </div>
                        ) }
                    </Form.Field>

                    {/* --- Question Text --- */}
                    <Form.Field>
                        <label>Question</label>
                        <TextArea
                            rows={ 3 }
                            value={ draft.questionText }
                            maxLength={ 255 }
                            placeholder="Enter your question..."
                            onChange={ ( _, d ) => setField( 'questionText', d.value as string ) }
                        />
                        <div style={ { textAlign: 'right', fontSize: '0.75em', color: '#999', marginTop: '4px' } }>
                            { draft.questionText.length } / 255
                        </div>
                    </Form.Field>

                    {/* --- Description --- */}
                    <Form.Field>
                        <label>
                            Description{ ' ' }
                            <span style={ { fontWeight: 'normal', color: '#999', fontSize: '0.9em' } }>(Optional)</span>
                        </label>
                        <TextArea
                            rows={ 2 }
                            value={ draft.description }
                            maxLength={ 500 }
                            placeholder="Add additional context for the respondent..."
                            onChange={ ( _, d ) => setField( 'description', d.value as string ) }
                        />
                        <div style={ { textAlign: 'right', fontSize: '0.75em', color: '#999', marginTop: '4px' } }>
                            { draft.description.length } / 500
                        </div>
                    </Form.Field>

                    {/* --- Answer Options --- */}
                    { hasOptions && (
                        <Form.Field>
                            <label>Answer Options</label>
                            <List>
                                { draft.options.map( ( option, index ) => (
                                    <List.Item
                                        key={ index }
                                        style={ { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' } }>
                                        <Icon name="bars" style={ { color: '#ccc', cursor: 'grab', flexShrink: 0 } }/>
                                        <Input
                                            fluid
                                            size="small"
                                            value={ option }
                                            placeholder={ `Option ${ index + 1 }` }
                                            onChange={ e => updateOption( index, e.target.value ) }
                                            style={ { flex: 1 } }
                                        />
                                        <Icon
                                            name="copy outline"
                                            link
                                            style={ { color: '#888', flexShrink: 0 } }
                                            onClick={ () => copyOption( index ) }
                                        />
                                        <Icon
                                            name="trash alternate outline"
                                            link
                                            style={ { color: '#e57373', flexShrink: 0 } }
                                            onClick={ () => removeOption( index ) }
                                        />
                                    </List.Item>
                                ) ) }
                            </List>
                            <Button type="button" basic size="small" onClick={ addOption }>
                                <Icon name="plus"/> Add Option
                            </Button>
                        </Form.Field>
                    ) }

                    {/* --- Follow-up Text Toggle (DROPDOWN / MULTISELECT only) --- */}
                   

                    {/* --- Required --- */}
                    <Form.Field>
                        <Checkbox
                            label="Required"
                            checked={ draft.isRequired }
                            onChange={ ( _, d ) => setField( 'isRequired', !!d.checked ) }
                        />
                    </Form.Field>

                    </Form>
                </Modal.Content>

                <Modal.Actions>
                    <Button type="button" basic onClick={ onClose }>Cancel</Button>
                    <Button
                        type="button"
                        color="purple"
                        disabled={ !draft.questionText.trim() }
                        onClick={ () => onSave( draft ) }>
                        Save Question
                    </Button>
                </Modal.Actions>

            </Modal>

            <Modal
                open={ isCreateTagOpen }
                size="tiny"
                onClose={ () => {
                    if( isCreatingTag ){
                        return;
                    }
                    setIsCreateTagOpen( false );
                } }>
                <Modal.Header>Add Survey Tag</Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <label>Tag Name</label>
                            <Input
                                autoFocus
                                placeholder="Enter tag name"
                                value={ newTagName }
                                onChange={ e => setNewTagName( e.target.value ) }
                            />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type="button"
                        basic
                        disabled={ isCreatingTag }
                        onClick={ () => {
                            setIsCreateTagOpen( false );
                            setNewTagName( '' );
                        } }>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        color="purple"
                        loading={ isCreatingTag }
                        disabled={ !newTagName.trim() || isCreatingTag }
                        onClick={ handleCreateTag }>
                        Add Tag
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
}

// --- Question Row ---

interface QuestionRowProps {
    question:        SurveyQuestion;
    surveyTagNames:  string[];
    onEdit:          ( question: SurveyQuestion ) => void;
    onDelete:        ( id: string ) => void;
}

function QuestionRow( { question, surveyTagNames, onEdit, onDelete }: QuestionRowProps ){

    const [ hovered, setHovered ] = useState( false );
    const typeBadge               = getTypeBadgeLabel( question );

    return (
        <div
            style={ {
                display:         'flex',
                alignItems:      'flex-start',
                gap:             '12px',
                padding:         '10px 12px 10px 14px',
                borderLeft:      '3px solid #d8d8d8',
                borderBottom:    '1px solid #f0f0f0',
                cursor:          'pointer',
                background:      hovered ? '#fafafa' : 'white',
                transition:      'background 0.12s',
            } }
            onClick={ () => onEdit( question ) }
            onMouseEnter={ () => setHovered( true ) }
            onMouseLeave={ () => setHovered( false ) }>

            <div style={ { flex: 1, minWidth: 0 } }>
                <div style={ { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '5px', flexWrap: 'wrap' } }>
                    { surveyTagNames.map( tagName => (
                        <Label key={ tagName } size="tiny" style={ { margin: 0 } }>{ tagName }</Label>
                    ) ) }
                    <Label size="tiny" basic style={ { margin: 0 } }>
                        { typeBadge }
                    </Label>
                    { question.isRequired && (
                        <Label size="tiny" basic color="red" style={ { margin: 0 } }>REQUIRED</Label>
                    ) }
                </div>
                <div style={ { fontSize: '0.875em', color: '#333', lineHeight: 1.45, wordBreak: 'break-word' } }>
                    { question.questionText }
                </div>
            </div>

            <div
                style={ { display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0, paddingTop: '2px' } }
                onClick={ e => e.stopPropagation() }>
                <Button
                    basic
                    size="mini"
                    color="teal"
                    type="button"
                    onClick={ () => onEdit( question ) }>
                    Open
                </Button>
                <Icon
                    name="trash alternate outline"
                    link
                    style={ { color: '#ccc' } }
                    onClick={ () => onDelete( question.id ) }
                />
            </div>

        </div>
    );
}


// --- Main Component ---

interface SurveyBuilderSectionProps {}

function SurveyBuilderSection( _props: SurveyBuilderSectionProps ){

    const { RunInsert, RunUpdate, RunDelete, TableSelect } = useCentralCalls();

    const [ questions, setQuestions ]             = useState<SurveyQuestion[]>( [] ),
          [ surveyTags, setSurveyTags ]           = useState<SurveyTag[]>( [] ),
          [ searchQuery, setSearchQuery ]         = useState( '' ),
          [ editingQuestion, setEditingQuestion ] = useState<SurveyQuestion | null>( null ),
          [ isModalOpen, setIsModalOpen ]         = useState( false ),
          [ isLoading, setIsLoading ]             = useState( true ),
          [ isSaving, setIsSaving ]               = useState( false );

    // Load all questions plus their options from the database.
    const loadQuestions = async () => {
        setIsLoading( true );
        try {
            const [ questionRows, surveyTagRows, questionTagRows ] = await Promise.all( [
                TableSelect( SURVEY_QUESTION_TABLE, {} ) as Promise<SurveyQuestionRow[]>,
                TableSelect( SURVEY_TAG_TABLE, {} ) as Promise<SurveyTagRow[]>,
                TableSelect( SURVEY_QUESTION_TAG_TABLE, {} ) as Promise<SurveyQuestionTagRow[]>,
            ] );

            const safeQuestionRows = questionRows ?? [];
            const safeSurveyTagRows = surveyTagRows ?? [];
            const safeQuestionTagRows = questionTagRows ?? [];

            const questionToTagIds: Record<string, string[]> = {};
            for( const row of safeQuestionTagRows ){
                if( !row.surveyQuestionId || !row.surveyTagId ){
                    continue;
                }

                if( !questionToTagIds[ row.surveyQuestionId ] ){
                    questionToTagIds[ row.surveyQuestionId ] = [];
                }

                questionToTagIds[ row.surveyQuestionId ].push( row.surveyTagId );
            }

            const loaded = await Promise.all(
                safeQuestionRows.map( async ( row ) => {
                    const optionRowsAll: SurveyQuestionOptionRow[] =
                        await TableSelect( SURVEY_QUESTION_OPTION_TABLE, { SurveyQuestionId: row.id } ) ?? [];
                    const optionRows = optionRowsAll.filter( option => !isRowDeleted( option.isDeleted ) );
                    return toLocalQuestion( row, optionRows, questionToTagIds[ row.id ] ?? [] );
                } ),
            );

            setQuestions( loaded );
            setSurveyTags( safeSurveyTagRows.sort( ( a, b ) => a.tagName.localeCompare( b.tagName ) ) );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to load questions:', err );
        } finally {
            setIsLoading( false );
        }
    };

    useEffect( () => {
        loadQuestions();
    }, [] );

    // Insert one option row per non-empty option string.
    const insertOptionsForQuestion = async ( questionId: string, options: string[] ) => {
        for( const optionText of options ){
            await RunInsert( SURVEY_QUESTION_OPTION_TABLE, {
                Id:               uuid(),
                SurveyQuestionId: questionId,
                OptionText:       optionText,
            } );
        }
    };

    // For existing questions, soft-delete removed options and add/restore selected options.
    const syncOptionsForQuestion = async ( questionId: string, options: string[] ) => {
        const existingOptions: SurveyQuestionOptionRow[] =
            await TableSelect( SURVEY_QUESTION_OPTION_TABLE, { SurveyQuestionId: questionId } ) ?? [];

        const activeOptionsByKey = new Map<string, SurveyQuestionOptionRow>();
        const deletedOptionsByKey = new Map<string, SurveyQuestionOptionRow>();

        for( const option of existingOptions ){
            const key = normalizeOptionText( option.optionText );
            if( !key ){
                continue;
            }

            if( isRowDeleted( option.isDeleted ) ){
                if( !deletedOptionsByKey.has( key ) ){
                    deletedOptionsByKey.set( key, option );
                }
            } else if( !activeOptionsByKey.has( key ) ){
                activeOptionsByKey.set( key, option );
            }
        }

        const uniqueDesiredByKey = new Map<string, string>();
        for( const optionText of options ){
            const trimmed = optionText.trim();
            const key = normalizeOptionText( trimmed );
            if( !key ){
                continue;
            }

            if( !uniqueDesiredByKey.has( key ) ){
                uniqueDesiredByKey.set( key, trimmed );
            }
        }

        for( const [ key, optionText ] of uniqueDesiredByKey ){
            if( activeOptionsByKey.has( key ) ){
                continue;
            }

            const deletedOption = deletedOptionsByKey.get( key );
            if( deletedOption ){
                await RunUpdate( SURVEY_QUESTION_OPTION_TABLE, deletedOption.id, {
                    OptionText: optionText,
                    IsDeleted:  false,
                } );
                continue;
            }

            await RunInsert( SURVEY_QUESTION_OPTION_TABLE, {
                Id:               uuid(),
                SurveyQuestionId: questionId,
                OptionText:       optionText,
                IsDeleted:        false,
            } );
        }

        for( const [ key, option ] of activeOptionsByKey ){
            if( uniqueDesiredByKey.has( key ) ){
                continue;
            }

            await RunUpdate( SURVEY_QUESTION_OPTION_TABLE, option.id, { IsDeleted: true } );
        }
    };

    const deleteTagsForQuestion = async ( questionId: string ) => {
        const existingTags: SurveyQuestionTagRow[] =
            await TableSelect( SURVEY_QUESTION_TAG_TABLE, { SurveyQuestionId: questionId } ) ?? [];

        for( const tagRow of existingTags ){
            await RunDelete( SURVEY_QUESTION_TAG_TABLE, tagRow.id );
        }
    };

    const insertTagsForQuestion = async ( questionId: string, surveyTagIds: string[] ) => {
        const uniqueTagIds = [ ...new Set( surveyTagIds ) ];
        if( uniqueTagIds.length === 0 ){
            return;
        }

        for( const surveyTagId of uniqueTagIds ){
            await RunInsert( SURVEY_QUESTION_TAG_TABLE, {
                Id:               uuid(),
                SurveyQuestionId: questionId,
                SurveyTagId:      surveyTagId,
            } );
        }
    };

    // For existing questions, add missing links and remove unselected links.
    const syncTagsForQuestion = async ( questionId: string, surveyTagIds: string[] ) => {
        const existingTags: SurveyQuestionTagRow[] =
            await TableSelect( SURVEY_QUESTION_TAG_TABLE, { SurveyQuestionId: questionId } ) ?? [];

        const desiredTagIds = new Set( surveyTagIds.filter( id => !!id ) );
        const existingTagIds = new Set( existingTags.map( row => row.surveyTagId ).filter( ( id ): id is string => !!id ) );

        for( const surveyTagId of desiredTagIds ){
            if( existingTagIds.has( surveyTagId ) ){
                continue;
            }

            await RunInsert( SURVEY_QUESTION_TAG_TABLE, {
                Id:               uuid(),
                SurveyQuestionId: questionId,
                SurveyTagId:      surveyTagId,
            } );
        }

        for( const existingTag of existingTags ){
            if( !existingTag.surveyTagId || desiredTagIds.has( existingTag.surveyTagId ) ){
                continue;
            }

            await RunDelete( SURVEY_QUESTION_TAG_TABLE, existingTag.id );
        }
    };

    const handleCreateTag = async ( tagName: string ): Promise<string | null> => {
        const trimmedName = tagName.trim();
        if( !trimmedName ){
            return null;
        }

        try {
            const createdTagId = uuid();
            await RunInsert( SURVEY_TAG_TABLE, {
                Id:      createdTagId,
                TagName: trimmedName,
            } );

            setSurveyTags( prev => [ ...prev, { id: createdTagId, tagName: trimmedName } ]
                .sort( ( a, b ) => a.tagName.localeCompare( b.tagName ) ) );

            return createdTagId;
        } catch( err ){
            console.error( '[SurveyBuilder] failed to create tag:', err );
            return null;
        }
    };

    const tagsById = useMemo( () => {
        return surveyTags.reduce<Record<string, SurveyTag>>( ( acc, tag ) => {
            acc[ tag.id ] = tag;
            return acc;
        }, {} );
    }, [ surveyTags ] );

    const filteredQuestions = questions.filter( q => {
        const searchValue = searchQuery.trim().toLowerCase();
        if( !searchValue ){
            return true;
        }

        const tagNames = q.surveyTagIds.map( tagId => tagsById[ tagId ]?.tagName ?? '' );
        const hasTagMatch = tagNames.some( tagName => tagName.toLowerCase().includes( searchValue ) );
        return q.questionText.toLowerCase().includes( searchValue ) || hasTagMatch;
    } );

    const handleAddQuestion = () => {
        setEditingQuestion( null );
        setIsModalOpen( true );
    };

    const handleEditQuestion = ( question: SurveyQuestion ) => {
        setEditingQuestion( question );
        setIsModalOpen( true );
    };

    const handleSaveQuestion = async ( question: SurveyQuestion ) => {
        const isExisting  = questions.some( q => q.id === question.id );
        const cleanOptions = question.options.filter( o => o.trim() !== '' );

        // Payload keys are the DB column names (PascalCase); values come from the UI model.
        const columns = {
            QuestionType: question.type,
            Question:     question.questionText,
            Description:  question.description || null,
            Required:     question.isRequired,
        };

        setIsSaving( true );
        try {
            if( isExisting ){
                await RunUpdate( SURVEY_QUESTION_TABLE, question.id, columns );
                await syncOptionsForQuestion( question.id, cleanOptions );
                await syncTagsForQuestion( question.id, question.surveyTagIds );

                setQuestions( prev => prev.map( q => q.id === question.id ? question : q ) );
            } else {
                await RunInsert( SURVEY_QUESTION_TABLE, { Id: question.id, ...columns } );
                await insertOptionsForQuestion( question.id, cleanOptions );
                await insertTagsForQuestion( question.id, question.surveyTagIds );

                setQuestions( prev => [ ...prev, question ] );
            }

            setIsModalOpen( false );
            setEditingQuestion( null );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to save question:', err );
        } finally {
            setIsSaving( false );
        }
    };

    const handleDeleteQuestion = async ( id: string ) => {
        setIsSaving( true );
        try {
            await deleteTagsForQuestion( id );
            await RunDelete( SURVEY_QUESTION_TABLE, id );

            setQuestions( prev => prev.filter( q => q.id !== id ) );
        } catch( err ){
            console.error( '[SurveyBuilder] failed to delete question:', err );
        } finally {
            setIsSaving( false );
        }
    };

    const requiredCount = questions.filter( q => q.isRequired ).length;

    if( isLoading ){
        return <LoadingIndicator text="Loading questions..."/>;
    }

    return (
        <>
            <EditQuestionModal
                open={ isModalOpen }
                question={ editingQuestion }
                availableTags={ surveyTags }
                onClose={ () => setIsModalOpen( false ) }
                onSave={ handleSaveQuestion }
                onCreateTag={ handleCreateTag }
            />

            <div style={ { display: 'flex', flexDirection: 'column', height: '100%' } }>

                {/* ── Row 1: inline stats + create button ── */}
                <div style={ {
                    display:       'flex',
                    alignItems:    'center',
                    justifyContent: 'space-between',
                    marginBottom:  '10px',
                    paddingBottom: '10px',
                    borderBottom:  '1px solid #ebebeb',
                } }>
                    <div style={ { display: 'flex', alignItems: 'center', gap: '0' } }>
                        { [
                            { value: questions.length, label: 'Questions' },
                            { value: surveyTags.length, label: 'Tags' },
                            { value: requiredCount, label: 'Required' },
                        ].map( ( stat, i ) => (
                            <div
                                key={ stat.label }
                                style={ {
                                    paddingRight: i < 2 ? '16px' : 0,
                                    marginRight:  i < 2 ? '16px' : 0,
                                    borderRight:  i < 2 ? '1px solid #e0e0e0' : 'none',
                                } }>
                                <span style={ { fontSize: '1.25em', fontWeight: 700, color: '#333' } }>
                                    { stat.value }
                                </span>
                                <span style={ { fontSize: '0.78em', color: '#aaa', marginLeft: '5px' } }>
                                    { stat.label }
                                </span>
                            </div>
                        ) ) }
                    </div>

                    <Button color="purple" size="small" type="button" disabled={ isSaving } onClick={ handleAddQuestion }>
                        <Icon name="plus"/> Create question
                    </Button>
                </div>

                {/* ── Row 2: search ── */}
                <div style={ {
                    display:     'flex',
                    alignItems:  'center',
                    gap:         '8px',
                    marginBottom: '10px',
                    justifyContent: 'flex-end',
                } }>
                    <Input
                        size="small"
                        icon="search"
                        iconPosition="left"
                        placeholder="Search questions or tags..."
                        value={ searchQuery }
                        onChange={ e => setSearchQuery( e.target.value ) }
                        style={ { width: '260px', flexShrink: 0 } }
                    />
                </div>

                {/* ── Question list ── */}
                <div style={ {
                    flex:       1,
                    overflowY:  'auto',
                    border:     '1px solid #ebebeb',
                    borderRadius: '4px',
                    background: 'white',
                } }>
                    { filteredQuestions.length === 0 ? (
                        <EmptyCollectionMessage
                            icon="question circle outline"
                            header="No questions found"
                            content="Add a question using the button above, or adjust your filters."
                        />
                    ) : (
                          filteredQuestions.map( question => (
                              <QuestionRow
                                  key={ question.id }
                                  question={ question }
                                  surveyTagNames={ question.surveyTagIds
                                      .map( tagId => tagsById[ tagId ]?.tagName )
                                      .filter( ( name ): name is string => !!name ) }
                                  onEdit={ handleEditQuestion }
                                  onDelete={ handleDeleteQuestion }
                              />
                          ) )
                      ) }
                </div>

            </div>
        </>
    );
}


export {
    SurveyBuilderSection,
    type SurveyBuilderSectionProps,
};
